<!--
  隐私政策 / 用户协议（同一个组件，按 type 切换）。

  ⚠️ 上线前必须替换 CONTACT_EMAIL 为真实可用的联系邮箱 —— 隐私政策里没有
     有效的联系方式，等于没有提供「行使权利」的渠道，反而不合规。

  ⚠️ 内容必须与实现保持一致。改动了收集范围（新增字段、接入新的第三方服务）
     就要同步改这里，否则属于「告知不实」。当前文案对应：
       - users 表：邮箱、密码哈希、昵称、头像、注册/登录 IP 与时间
       - 内容：时间块、随想、好友关系、分享、评论、点赞、成就
       - 审计：admin_operation_logs（管理端操作）、运行日志（含请求 IP）
       - 第三方：又拍云（图片存储）、飞书（异常告警推送）、友盟（统计 / APM / 推送 / 广告）

  ⚠️ v3（2026-10-28）随合规要求更新：友盟段落补全监管要求的披露项 —— 运营方
     （友盟同欣（北京）科技有限公司）、收集的具体个人信息类型、官方隐私政策链接、
     用户权益保障机制（友盟隐私专职邮箱）。**两端文案必须同步改**
     （`mobile_app/lib/pages/legal_page.dart`）。

  为什么不放进弹窗：需要可被搜索引擎/应用商店审核直接访问的独立 URL，
  所以做成两个真实路由 /privacy 与 /terms。
-->
<template>
  <div class="legal-page">
    <div class="legal-wrap">
      <div class="legal-top">
        <a class="legal-back" href="/" @click.prevent="goHome">
          <i class="el-icon-arrow-left" />返回首页
        </a>
        <span class="legal-switch">
          <a :class="{ 'is-active': type !== 'privacy' }" href="/terms" @click.prevent="go('terms')">用户协议</a>
          <em>·</em>
          <a :class="{ 'is-active': type === 'privacy' }" href="/privacy" @click.prevent="go('privacy')">隐私政策</a>
        </span>
      </div>

      <template v-if="type === 'privacy'">
        <h1>隐私政策</h1>
        <p class="legal-meta">生效日期：2026 年 10 月 28 日 · 版本 v3</p>

        <p>
          Task Block（以下简称「本服务」）由个人开发者运营。我们深知个人信息对你的重要性，
          并会尽力保护你的个人信息安全可靠。本政策说明我们收集哪些信息、如何使用与保存，
          以及你可以行使哪些权利。
        </p>

        <h2>一、我们收集的信息</h2>
        <p>我们只收集实现功能所必需的信息，不收集与功能无关的内容。</p>
        <ul>
          <li><b>账号信息</b>：邮箱地址、密码。密码仅以不可逆的加密形式保存，我们无法看到你的明文密码。</li>
          <li><b>资料信息</b>：昵称、头像、背景图（均为选填，可随时修改或清空）。</li>
          <li><b>你创建的内容</b>：时间块、随想、标签、工作区、好友关系、分享、评论、点赞等。</li>
          <li><b>设备与使用信息</b>：注册与登录时使用的 IP 地址、登录时间、注册来源。
            这类信息仅用于安全防护（登录限流、异常登录排查）与故障定位。</li>
          <li><b>广告相关信息</b>：为展示广告并统计效果，广告服务（友盟 U-AppWin）会读取
            设备标识符（如系统允许获取的 OAID 等）、设备型号与系统版本、网络类型，
            以及已安装应用列表用于反作弊与渠道归因。
            这类信息用于广告投放与效果统计，<b>不与</b>你的时间块、随想等内容关联。</li>
        </ul>

        <h2>二、我们如何使用这些信息</h2>
        <ul>
          <li>提供与维护核心功能（记录时间块、同步数据、好友可见性控制）。</li>
          <li>保障账号与服务安全：登录失败次数限制、访问频率限制、异常行为审计。</li>
          <li>排查故障：出现异常时，系统会记录出错的功能、错误详情与请求 IP。</li>
        </ul>
        <p>我们<b>不会</b>出售或出租你的个人信息，也<b>不会</b>将你的时间块、随想、评论等内容
          用于广告投放 —— 广告只依据设备层面的标识投放，与你的内容无关。</p>

        <h2>三、信息的存储</h2>
        <ul>
          <li><b>存储地点</b>：数据存储于中华人民共和国境内的服务器。</li>
          <li><b>保存期限</b>：账号存续期间持续保存。注销账号后昵称与头像立即匿名化，身份信息进入保留期（默认 30 天），期满自动清空（见第五条）。</li>
          <li><b>操作记录</b>：管理端操作记录按系统设置保留（默认 90 天）后自动清理；
            系统运行记录保留 14 天，其中仅出错记录保留 30 天。</li>
        </ul>

        <h2>四、信息的共享与委托处理</h2>
        <p>为实现功能，我们接入了以下第三方服务，仅在必要范围内提供数据：</p>
        <ul>
          <li><b>又拍云</b>：存储你上传的头像与背景图。</li>
          <li><b>飞书（Lark）</b>：接收系统异常提醒。推送内容仅包含错误类型、出错功能与出错位置，
            <b>不包含</b>你的手机号、邮箱或密码。</li>
          <li><b>友盟（Umeng）</b>：由友盟同欣（北京）科技有限公司运营，为本服务提供
            U-App 统计分析、U-APM 崩溃与性能监控、U-Push 消息推送与 U-AppWin 广告展示。
            其 SDK 会收集设备标识符（如 OAID / Android ID / IDFA）、设备型号与系统版本、
            网络信息、已安装应用列表，以及崩溃堆栈与页面性能数据、设备推送标识、广告交互记录；
            其中位置信息为可选收集，仅用于反作弊。上述信息用于统计分析、故障排查、消息推送与广告投放，
            <b>不与</b>你的时间块、随想等内容关联。
            友盟隐私政策详见 https://www.umeng.com/page/policy；
            如需就友盟收集的信息行使查阅、更正或删除等权利，可联系其隐私专职邮箱
            Umeng_Legal@service.umeng.com，或通过下方邮箱与我们联系。</li>
        </ul>
        <p>除上述情形及法律法规要求外，我们不会向任何第三方提供你的个人信息。</p>

        <h2>五、你的权利</h2>
        <ul>
          <li><b>查询与更正</b>：登录后可在「个人中心」查看并修改昵称、头像、邮箱、密码。</li>
          <li><b>删除（注销账号）</b>：在「个人中心 → 个人资料 → 注销账号」提交，需输入当前密码确认。
            提交后账号<b>立即停用</b>：无法再登录，所有设备退出登录，昵称改为「已注销用户」。
            你的手机号、邮箱、加密后的密码与 IP 记录会在保留期（默认 30 天）内继续保存，
            用于处理误操作找回与安全事件追溯；期满后由系统自动清空。该操作<b>不可恢复</b>。</li>
          <li><b>撤回同意</b>：你可以通过注销账号来撤回对本政策的同意。撤回后我们将停止处理你的个人信息。</li>
        </ul>
        <p>
          注销后你创建的内容（时间块、随想、评论等）会以匿名形式保留，用于维持好友侧数据的完整性
          —— 这些内容已不再关联到你的身份。如需一并删除，请通过下方邮箱联系我们。
        </p>

        <h2>六、未成年人保护</h2>
        <p>本服务不面向 14 周岁以下的未成年人。若你是未成年人，请在监护人陪同下阅读本政策并使用本服务。</p>

        <h2>七、本政策的更新</h2>
        <p>本政策可能随功能调整而更新。涉及收集范围或使用目的的重大变更，我们会在站内显著位置提示。</p>

        <h2>八、联系我们</h2>
        <p>对本政策或个人信息处理有任何疑问、意见或投诉，请联系：<b>{{ contactEmail }}</b>。</p>
      </template>

      <template v-else>
        <h1>用户协议</h1>
        <p class="legal-meta">生效日期：2026 年 10 月 28 日 · 版本 v3</p>

        <p>欢迎使用 Task Block。在使用前，请仔细阅读本协议。你注册或使用本服务，即表示同意本协议全部内容。</p>

        <h2>一、服务内容</h2>
        <p>
          本服务提供时间记录、随想记录、好友可见性分享等功能。服务由个人开发者维护，
          可能因升级、维护或不可抗力而中断，我们会尽量提前告知。
        </p>

        <h2>二、账号与安全</h2>
        <ul>
          <li>你应提供真实、有效的邮箱用于注册，并妥善保管密码。</li>
          <li>账号仅限本人使用，不得转让、出借或出售。</li>
          <li>因你自身原因（密码泄露、设备丢失等）导致的损失，由你自行承担。</li>
          <li>发现账号被他人使用时，请立即修改密码或联系我们。</li>
        </ul>

        <h2>三、使用规范</h2>
        <p>你承诺不利用本服务从事以下行为：</p>
        <ul>
          <li>发布违反法律法规、公序良俗的内容；</li>
          <li>侵犯他人知识产权、隐私权、名誉权等合法权益；</li>
          <li>对服务进行破解、探测漏洞、恶意攻击或绕过访问限制；</li>
          <li>使用自动化工具批量注册、批量访问，影响服务正常运行。</li>
        </ul>
        <p>违反上述约定的，我们有权限制功能、暂停或终止你的账号。</p>

        <h2>四、内容与知识产权</h2>
        <ul>
          <li>你创建的内容归你所有。为提供服务，你授权我们在必要范围内存储、传输与展示这些内容。</li>
          <li>本服务的程序、界面设计与文档归开发者所有，未经许可不得复制或用于商业目的。</li>
        </ul>

        <h2>五、免责声明</h2>
        <p>
          本服务按「现状」提供。我们会尽力保障数据安全与可用性，但不对因服务中断、
          数据丢失、第三方服务故障造成的间接损失承担责任。请勿将本服务作为唯一的数据备份手段。
        </p>

        <h2>六、服务变更与终止</h2>
        <p>
          你可以随时通过「个人中心 → 个人资料 → 注销账号」终止使用本服务。
          若服务整体停止运营，我们会提前在站内公告，并为你保留合理的导出时间。
        </p>

        <h2>七、协议修改</h2>
        <p>本协议可能随服务调整而更新。更新后的协议在站内公布即生效，继续使用即视为接受。</p>

        <h2>八、联系方式</h2>
        <p>与本协议相关的疑问，请联系：<b>{{ contactEmail }}</b>。</p>
      </template>

      <div class="legal-foot">
        本文档不构成法律意见。如需对外正式运营，建议由专业人士复核。
      </div>
    </div>
  </div>
</template>

<script>
import router from '@/router'

/**
 * 联系邮箱：⚠️ 上线前必须替换为真实可用的地址。
 * 隐私政策要求提供「行使权利」的渠道，留空等于不合规。
 */
const CONTACT_EMAIL = 'todo@suisuisu.cn'

export default {
  name: 'LegalPage',
  props: {
    /** 'privacy' | 'terms' */
    type: { type: String, default: 'privacy' }
  },
  computed: {
    contactEmail() { return CONTACT_EMAIL }
  },
  methods: {
    // 自研路由（无 vue-router），跳转统一走 @/router 的 go()
    go(type) {
      router.go(type === 'privacy' ? '/privacy' : '/terms')
    },
    goHome() {
      router.go('/')
    }
  }
}
</script>

<style scoped>
.legal-page {
  min-height: 100vh;
  background: #f7f8fa;
  padding: 32px 16px 64px;
  box-sizing: border-box;
}

.legal-wrap {
  max-width: 760px;
  margin: 0 auto;
  background: #fff;
  border-radius: 12px;
  padding: 36px 40px 32px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  color: #303133;
  font-size: 14px;
  line-height: 1.85;
}

.legal-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 13px;
}

.legal-back {
  color: #909399;
  text-decoration: none;
}

.legal-back:hover {
  color: #409eff;
}

.legal-switch a {
  color: #909399;
  text-decoration: none;
  padding: 0 2px;
}

.legal-switch a.is-active {
  color: #409eff;
  font-weight: 600;
}

.legal-switch em {
  color: #dcdfe6;
  font-style: normal;
  margin: 0 6px;
}

.legal-wrap h1 {
  font-size: 22px;
  margin: 12px 0 6px;
}

.legal-meta {
  color: #a8abb2;
  font-size: 12px;
  margin: 0 0 20px;
}

.legal-wrap h2 {
  font-size: 15px;
  margin: 26px 0 8px;
  padding-left: 10px;
  border-left: 3px solid #409eff;
  line-height: 1.4;
}

.legal-wrap p {
  margin: 8px 0;
}

.legal-wrap ul {
  margin: 8px 0;
  padding-left: 20px;
}

.legal-wrap li {
  margin: 4px 0;
}

.legal-foot {
  margin-top: 32px;
  padding-top: 14px;
  border-top: 1px dashed #e4e7ed;
  color: #b1b3b8;
  font-size: 12px;
}

@media (max-width: 640px) {
  .legal-page { padding: 16px 8px 40px; }
  .legal-wrap { padding: 24px 18px; border-radius: 8px; }
  .legal-wrap h1 { font-size: 19px; }
}
</style>
